import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { syllabusApi } from "../config/syllabusApi";
import Toast from "../Toast/Toast";
import "./CurrentAffairsQuizAdmin.css";

const DEFAULT_FORM = {
  totalQuestionsPerTopic: 100,
  easyCount: 20,
  mediumCount: 40,
  hardCount: 40,
  language: "HINDI",
  marks: 1,
  negativeMarks: 0,
  isPremium: false,
  createdBy: "OPENAI_ADMIN_BATCH",
  autoGenerateTestSeries: true,
  stopOnFirstFailure: false,
  subjects: [],
};

const formatValue = (value) => (value == null || value === "" ? "NA" : String(value));

const readNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const readBoolean = (...values) => {
  for (const value of values) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
    }
  }
  return false;
};

const readExamKey = (item) =>
  String(item?.examKey || item?.examCode || item?.exam_code || item?.key || "").trim();

const readExamName = (item) =>
  String(item?.examName || item?.exam_name || item?.title || "").trim();

const getErrorMessage = (error, fallback) => {
  if (String(error?.message || "").trim()) return String(error.message).trim();
  return fallback;
};

const normalizeOverviewRow = (row) => {
  const base = row && typeof row === "object" && !Array.isArray(row) ? row : {};
  return {
    ...base,
    generatedSeriesCount: readNumber(base?.generatedSeriesCount),
    additionalGeneratableSeriesCount: readNumber(base?.additionalGeneratableSeriesCount),
    totalGeneratableSeriesCount: readNumber(base?.totalGeneratableSeriesCount),
    totalGeneratableFullTestCount: readNumber(base?.totalGeneratableFullTestCount),
    totalGeneratableSectionalTestCount: readNumber(base?.totalGeneratableSectionalTestCount),
    totalGeneratablePyqTestCount: readNumber(base?.totalGeneratablePyqTestCount),
  };
};

const normalizeOverviewPayload = (payload) => {
  if (Array.isArray(payload)) return normalizeOverviewRow(payload[0]);
  if (Array.isArray(payload?.items)) return normalizeOverviewRow(payload.items[0]);
  if (Array.isArray(payload?.overview)) return normalizeOverviewRow(payload.overview[0]);
  if (Array.isArray(payload?.data)) return normalizeOverviewRow(payload.data[0]);
  return normalizeOverviewRow(payload?.data || payload);
};

const readCoverageSubjects = (payload) => {
  if (Array.isArray(payload?.subjects)) return payload.subjects;
  if (Array.isArray(payload?.data?.subjects)) return payload.data.subjects;
  if (Array.isArray(payload?.coverage?.subjects)) return payload.coverage.subjects;
  return [];
};

const normalizeBlueprintRows = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.papers)) return value.papers;
  if (Array.isArray(value?.subjects)) return value.subjects;
  if (Array.isArray(value?.blueprint)) return value.blueprint;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const normalizeCoverageRows = (coveragePayload, targetPerTopic) => {
  return readCoverageSubjects(coveragePayload).flatMap((subject, subjectIndex) => {
    const subjectName = String(
      subject?.subjectName || subject?.subject_name || `Subject ${subjectIndex + 1}`
    ).trim();
    const topics = Array.isArray(subject?.topics) ? subject.topics : [];

    return topics.map((topic, topicIndex) => {
      const availableQuestionsInExam = readNumber(topic?.availableQuestionsInExam);
      return {
        subject: subjectName,
        topic: String(topic?.topicName || topic?.topic || `Topic ${topicIndex + 1}`).trim(),
        availableQuestionsInExam,
        availableQuestionsOverall: readNumber(topic?.availableQuestionsOverall),
        deficit: Math.max(0, readNumber(targetPerTopic) - availableQuestionsInExam),
      };
    });
  });
};

const normalizeBlueprintCards = (payload) => {
  return normalizeBlueprintRows(payload).map((row, index) => ({
    id: `${row?.paperName || row?.subjectName || "blueprint"}-${index}`,
    title:
      row?.paperName || row?.paper || row?.subjectName || row?.subject || `Section ${index + 1}`,
    availableQuestionsForSubject: readNumber(row?.availableQuestionsForSubject),
    questionsPerSeries: readNumber(row?.questionsPerSeries),
    normalizedTopics: Array.isArray(row?.normalizedTopics) ? row.normalizedTopics : [],
  }));
};

const normalizeTopicResult = (row) => {
  const base = row && typeof row === "object" && !Array.isArray(row) ? row : {};
  const persistedTotal = readNumber(
    base?.persistedTotal,
    base?.totalPersisted,
    base?.persistedCreated + base?.persistedUpdated,
    base?.createdCount + base?.updatedCount
  );

  return {
    subject: String(base?.subject || base?.subjectName || "").trim(),
    topic: String(base?.topic || base?.topicName || "").trim(),
    requestedTotal: readNumber(
      base?.requestedTotal,
      base?.requested,
      base?.requestedQuestions,
      base?.requestedQuestionCount
    ),
    generatedCount: readNumber(
      base?.generatedCount,
      base?.generated,
      base?.generatedQuestions,
      base?.generatedQuestionCount
    ),
    persistedTotal,
    complete: readBoolean(base?.complete, base?.isComplete),
    success: readBoolean(base?.success, base?.isSuccess, base?.status === "SUCCESS"),
    error: String(base?.error || base?.errorMessage || "").trim(),
  };
};

const normalizeRunResult = (payload) => {
  const raw = payload?.data || payload || {};
  const topicResults = Array.isArray(raw?.topicResults) ? raw.topicResults.map(normalizeTopicResult) : [];
  const testSeriesGeneration = raw?.testSeriesGeneration || {};

  return {
    message: String(raw?.message || "").trim(),
    requestedTopicsCount: readNumber(raw?.requestedTopicsCount),
    processedTopicsCount: readNumber(raw?.processedTopicsCount),
    successfulTopicsCount: readNumber(raw?.successfulTopicsCount),
    failedTopicsCount: readNumber(raw?.failedTopicsCount),
    totalQuestionsRequested: readNumber(raw?.totalQuestionsRequested),
    totalQuestionsGenerated: readNumber(raw?.totalQuestionsGenerated),
    totalQuestionsPersisted: readNumber(raw?.totalQuestionsPersisted),
    totalSkippedRunDuplicates: readNumber(raw?.totalSkippedRunDuplicates),
    artifactDownloadUrl: String(raw?.artifactDownloadUrl || "").trim(),
    artifactId: String(raw?.artifactId || "").trim(),
    topicResults,
    testSeriesGeneration: {
      generatedNowFullTestCount: readNumber(testSeriesGeneration?.generatedNowFullTestCount),
      generatedNowSectionalTestCount: readNumber(
        testSeriesGeneration?.generatedNowSectionalTestCount
      ),
      generatedNowPyqTestCount: readNumber(testSeriesGeneration?.generatedNowPyqTestCount),
    },
  };
};

function AdminSyllabusAutomationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const preferredExamKey = String(searchParams.get("examKey") || "").trim();
  const [examRows, setExamRows] = useState([]);
  const [selectedExamKey, setSelectedExamKey] = useState("");
  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingContext, setLoadingContext] = useState(false);
  const [running, setRunning] = useState(false);
  const [downloadingArtifact, setDownloadingArtifact] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [coveragePayload, setCoveragePayload] = useState(null);
  const [blueprintPayload, setBlueprintPayload] = useState(null);
  const [overviewPayload, setOverviewPayload] = useState(null);
  const [runResultPayload, setRunResultPayload] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadExams = async () => {
      setLoadingExams(true);
      try {
        const data = await syllabusApi.adminList();
        if (!mounted) return;
        const nextRows = Array.isArray(data) ? data : [];
        setExamRows(nextRows);
        setSelectedExamKey((current) => {
          if (current) return current;
          const preferredMatch = nextRows.find((row) => readExamKey(row) === preferredExamKey);
          if (preferredMatch) return readExamKey(preferredMatch);
          return readExamKey(nextRows[0]) || "";
        });
      } catch (err) {
        if (!mounted) return;
        setError(getErrorMessage(err, "Unable to load syllabus list."));
      } finally {
        if (mounted) setLoadingExams(false);
      }
    };

    loadExams();
    return () => {
      mounted = false;
    };
  }, [preferredExamKey]);

  useEffect(() => {
    if (!selectedExamKey) return;

    const currentExamKey = String(searchParams.get("examKey") || "").trim();
    if (currentExamKey === selectedExamKey) return;

    setSearchParams({ examKey: selectedExamKey }, { replace: true });
  }, [searchParams, selectedExamKey, setSearchParams]);

  useEffect(() => {
    if (!selectedExamKey) {
      setCoveragePayload(null);
      setBlueprintPayload(null);
      setOverviewPayload(null);
      return;
    }

    let mounted = true;

    const loadContext = async () => {
      setLoadingContext(true);
      setError("");

      try {
        const [blueprintData, coverageData, overviewData] = await Promise.all([
          syllabusApi.getTestSeriesBlueprint(selectedExamKey),
          syllabusApi.adminQuestionBankCoverage(selectedExamKey),
          syllabusApi.adminTestSeriesOverviewByExamKey(selectedExamKey),
        ]);

        if (!mounted) return;
        setBlueprintPayload(blueprintData);
        setCoveragePayload(coverageData);
        setOverviewPayload(overviewData);
      } catch (err) {
        if (!mounted) return;
        setError(getErrorMessage(err, "Unable to load syllabus automation context."));
      } finally {
        if (mounted) setLoadingContext(false);
      }
    };

    loadContext();
    return () => {
      mounted = false;
    };
  }, [selectedExamKey]);

  const selectedExam = useMemo(
    () => examRows.find((row) => readExamKey(row) === selectedExamKey) || null,
    [examRows, selectedExamKey]
  );
  const coverageRows = useMemo(
    () => normalizeCoverageRows(coveragePayload, form.totalQuestionsPerTopic),
    [coveragePayload, form.totalQuestionsPerTopic]
  );
  const blueprintCards = useMemo(() => normalizeBlueprintCards(blueprintPayload), [blueprintPayload]);
  const overview = useMemo(() => normalizeOverviewPayload(overviewPayload), [overviewPayload]);
  const runResult = useMemo(
    () => (runResultPayload ? normalizeRunResult(runResultPayload) : null),
    [runResultPayload]
  );
  const availableSubjects = useMemo(() => {
    const subjectSet = new Set(coverageRows.map((row) => row.subject).filter(Boolean));
    return Array.from(subjectSet);
  }, [coverageRows]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubjectToggle = (subjectName) => {
    setForm((current) => {
      const exists = current.subjects.includes(subjectName);
      return {
        ...current,
        subjects: exists
          ? current.subjects.filter((item) => item !== subjectName)
          : [...current.subjects, subjectName],
      };
    });
  };

  const handleRun = async () => {
    const totalQuestionsPerTopic = readNumber(form.totalQuestionsPerTopic);
    const easyCount = readNumber(form.easyCount);
    const mediumCount = readNumber(form.mediumCount);
    const hardCount = readNumber(form.hardCount);

    if (!selectedExamKey) {
      setError("Select an exam first.");
      return;
    }

    if (easyCount + mediumCount + hardCount !== totalQuestionsPerTopic) {
      const nextError = "Easy, medium, and hard counts must equal total questions per topic.";
      setError(nextError);
      setToastMessage(nextError);
      return;
    }

    setRunning(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        totalQuestionsPerTopic,
        easyCount,
        mediumCount,
        hardCount,
        language: form.language,
        marks: readNumber(form.marks, 1),
        negativeMarks: Number(form.negativeMarks) || 0,
        isPremium: Boolean(form.isPremium),
        createdBy: String(form.createdBy || "OPENAI_ADMIN_BATCH").trim() || "OPENAI_ADMIN_BATCH",
        autoGenerateTestSeries: Boolean(form.autoGenerateTestSeries),
        stopOnFirstFailure: Boolean(form.stopOnFirstFailure),
        ...(form.subjects.length ? { subjects: form.subjects } : {}),
      };

      const result = await syllabusApi.adminGenerateAndSaveQuestionBank(selectedExamKey, payload);
      setRunResultPayload(result);
      setMessage(String(result?.message || "Automation completed."));
      setToastMessage("Syllabus automation finished.");

      const [blueprintData, coverageData, overviewData] = await Promise.all([
        syllabusApi.getTestSeriesBlueprint(selectedExamKey),
        syllabusApi.adminQuestionBankCoverage(selectedExamKey),
        syllabusApi.adminTestSeriesOverviewByExamKey(selectedExamKey),
      ]);
      setBlueprintPayload(blueprintData);
      setCoveragePayload(coverageData);
      setOverviewPayload(overviewData);
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to complete syllabus automation.");
      setError(nextError);
      setToastMessage(nextError);
    } finally {
      setRunning(false);
    }
  };

  const handleDownloadArtifact = async () => {
    if (!runResult) return;

    if (runResult.artifactDownloadUrl) {
      window.open(runResult.artifactDownloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (!runResult.artifactId) return;

    setDownloadingArtifact(true);
    try {
      const blob = await syllabusApi.adminDownloadQuestionBankArtifact(runResult.artifactId);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${selectedExamKey || "syllabus"}-automation-artifact.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to download artifact.");
      setError(nextError);
      setToastMessage(nextError);
    } finally {
      setDownloadingArtifact(false);
    }
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header">
        <h1>Syllabus Automation</h1>
        <div className="ca-admin-actions">
          <button type="button" className="ca-admin-btn" onClick={() => navigate("/admin/syllabus")}>
            Back To Syllabus
          </button>
        </div>
      </div>

      {message ? <p className="ca-admin-msg ok">{message}</p> : null}
      {error ? <p className="ca-admin-msg err">{error}</p> : null}
      {runResult?.failedTopicsCount ? (
        <p className="ca-admin-msg err">
          {runResult.failedTopicsCount} topic(s) failed. Backend failure email is sent automatically.
        </p>
      ) : null}

      <section className="ca-admin-list-card ca-admin-form-card">
        <div className="ca-admin-filter-group">
          <label htmlFor="syllabus-automation-exam">Exam</label>
          <select
            id="syllabus-automation-exam"
            value={selectedExamKey}
            disabled={loadingExams || running}
            onChange={(event) => setSelectedExamKey(event.target.value)}
          >
            <option value="">Select exam</option>
            {examRows.map((row, index) => {
              const examKey = readExamKey(row);
              return (
                <option key={`${examKey || "exam"}-${index}`} value={examKey}>
                  {readExamName(row) || examKey || "Untitled Exam"}
                </option>
              );
            })}
          </select>
        </div>

        <div className="ca-admin-stats-grid ca-admin-automation-summary-grid">
          <div className="ca-admin-stat-card">
            <span>Exam</span>
            <strong>{readExamName(selectedExam) || "NA"}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Exam Key</span>
            <strong>{selectedExamKey || "NA"}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Blueprint Sections</span>
            <strong>{blueprintCards.length}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Tracked Topics</span>
            <strong>{coverageRows.length}</strong>
          </div>
        </div>
      </section>

      <section className="ca-admin-list-card ca-admin-form-card" style={{ marginTop: 16 }}>
        <div className="ca-admin-header">
          <h2>Run Form</h2>
          {loadingContext ? <span className="ca-admin-status-badge loading">Loading</span> : null}
        </div>

        <div className="ca-admin-question-bank-form-grid">
          <div>
            <label htmlFor="automation-totalQuestionsPerTopic">Questions Per Topic</label>
            <input
              id="automation-totalQuestionsPerTopic"
              type="number"
              min="1"
              value={form.totalQuestionsPerTopic}
              disabled={running}
              onChange={(event) => handleChange("totalQuestionsPerTopic", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-easyCount">Easy</label>
            <input
              id="automation-easyCount"
              type="number"
              min="0"
              value={form.easyCount}
              disabled={running}
              onChange={(event) => handleChange("easyCount", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-mediumCount">Medium</label>
            <input
              id="automation-mediumCount"
              type="number"
              min="0"
              value={form.mediumCount}
              disabled={running}
              onChange={(event) => handleChange("mediumCount", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-hardCount">Hard</label>
            <input
              id="automation-hardCount"
              type="number"
              min="0"
              value={form.hardCount}
              disabled={running}
              onChange={(event) => handleChange("hardCount", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-language">Language</label>
            <input
              id="automation-language"
              value={form.language}
              disabled={running}
              onChange={(event) => handleChange("language", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-marks">Marks</label>
            <input
              id="automation-marks"
              type="number"
              min="0"
              step="0.01"
              value={form.marks}
              disabled={running}
              onChange={(event) => handleChange("marks", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-negativeMarks">Negative Marks</label>
            <input
              id="automation-negativeMarks"
              type="number"
              min="0"
              step="0.01"
              value={form.negativeMarks}
              disabled={running}
              onChange={(event) => handleChange("negativeMarks", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="automation-createdBy">Created By</label>
            <input
              id="automation-createdBy"
              value={form.createdBy}
              disabled={running}
              onChange={(event) => handleChange("createdBy", event.target.value)}
            />
          </div>
        </div>

        <div className="ca-admin-auto-grid">
          <label className="ca-admin-switch">
            <input
              type="checkbox"
              checked={form.autoGenerateTestSeries}
              disabled={running}
              onChange={(event) => handleChange("autoGenerateTestSeries", event.target.checked)}
            />
            <span className="ca-admin-switch-slider" />
            <span className="ca-admin-switch-label">Auto-generate test series</span>
          </label>
          <label className="ca-admin-switch">
            <input
              type="checkbox"
              checked={form.stopOnFirstFailure}
              disabled={running}
              onChange={(event) => handleChange("stopOnFirstFailure", event.target.checked)}
            />
            <span className="ca-admin-switch-slider" />
            <span className="ca-admin-switch-label">Stop on first failure</span>
          </label>
        </div>

        <label className="ca-admin-checkbox">
          <input
            type="checkbox"
            checked={form.isPremium}
            disabled={running}
            onChange={(event) => handleChange("isPremium", event.target.checked)}
          />
          <span>Premium questions</span>
        </label>

        <div className="ca-admin-automation-subjects">
          <div className="ca-admin-header" style={{ marginBottom: 8 }}>
            <div>
              <h3>Subjects</h3>
              <p className="ca-admin-subtitle">Leave empty to run all subjects.</p>
            </div>
          </div>

          <div className="ca-admin-chip-grid">
            {availableSubjects.map((subjectName) => (
              <button
                key={subjectName}
                type="button"
                className={`ca-admin-chip ${form.subjects.includes(subjectName) ? "active" : ""}`}
                disabled={running}
                onClick={() => handleSubjectToggle(subjectName)}
              >
                {subjectName}
              </button>
            ))}
          </div>
        </div>

        <div className="ca-admin-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="ca-admin-btn ca-admin-btn-primary"
            disabled={running || loadingContext || !selectedExamKey}
            onClick={handleRun}
          >
            {running ? "Running..." : "Run Automation"}
          </button>
          {runResult ? (
            <button type="button" className="ca-admin-btn" disabled={running} onClick={handleRun}>
              Retry
            </button>
          ) : null}
        </div>
      </section>

      <section className="ca-admin-list-card" style={{ marginTop: 16 }}>
        <div className="ca-admin-header">
          <h2>Before Run</h2>
        </div>

        <div className="ca-admin-stats-grid ca-admin-automation-summary-grid">
          <div className="ca-admin-stat-card">
            <span>Total Full Tests</span>
            <strong>{formatValue(overview?.totalGeneratableFullTestCount)}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Total Sectional Tests</span>
            <strong>{formatValue(overview?.totalGeneratableSectionalTestCount)}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Total PYQ Tests</span>
            <strong>{formatValue(overview?.totalGeneratablePyqTestCount)}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Questions Per Series</span>
            <strong>{formatValue(overview?.questionsPerSeries)}</strong>
          </div>
        </div>

        <div className="ca-admin-table-wrap" style={{ marginTop: 16 }}>
          <table className="ca-admin-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Topic</th>
                <th>Available In Exam</th>
                <th>Available Overall</th>
                <th>Deficit</th>
              </tr>
            </thead>
            <tbody>
              {coverageRows.map((row, index) => (
                <tr key={`${row.subject}-${row.topic}-${index}`}>
                  <td data-label="Subject">{row.subject}</td>
                  <td data-label="Topic">{row.topic}</td>
                  <td data-label="Available In Exam">{row.availableQuestionsInExam}</td>
                  <td data-label="Available Overall">{row.availableQuestionsOverall}</td>
                  <td data-label="Deficit">{row.deficit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {blueprintCards.length ? (
          <div className="ca-admin-automation-blueprint-grid">
            {blueprintCards.map((card) => (
              <article key={card.id} className="ca-admin-tool-card">
                <h3>{card.title}</h3>
                <p className="ca-admin-tool-copy">
                  Available Questions: <strong>{card.availableQuestionsForSubject}</strong>
                </p>
                <p className="ca-admin-tool-copy">
                  Questions Per Series: <strong>{card.questionsPerSeries}</strong>
                </p>
                <p className="ca-admin-tool-copy">
                  Normalized Topics:{" "}
                  <strong>{card.normalizedTopics.length ? card.normalizedTopics.join(", ") : "NA"}</strong>
                </p>
              </article>
            ))}
          </div>
        ) : null}
      </section>

      {runResult ? (
        <section className="ca-admin-list-card" style={{ marginTop: 16 }}>
          <div className="ca-admin-header">
            <div>
              <h2>Run Result</h2>
              {runResult.message ? <p className="ca-admin-subtitle">{runResult.message}</p> : null}
            </div>
            {(runResult.artifactDownloadUrl || runResult.artifactId) ? (
              <button
                type="button"
                className="ca-admin-btn"
                disabled={downloadingArtifact}
                onClick={handleDownloadArtifact}
              >
                {downloadingArtifact ? "Downloading..." : "Download Artifact"}
              </button>
            ) : null}
          </div>

          <div className="ca-admin-stats-grid ca-admin-automation-summary-grid">
            <div className="ca-admin-stat-card">
              <span>Requested Topics</span>
              <strong>{runResult.requestedTopicsCount}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Processed Topics</span>
              <strong>{runResult.processedTopicsCount}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Successful Topics</span>
              <strong>{runResult.successfulTopicsCount}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Failed Topics</span>
              <strong>{runResult.failedTopicsCount}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Total Requested</span>
              <strong>{runResult.totalQuestionsRequested}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Total Generated</span>
              <strong>{runResult.totalQuestionsGenerated}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Total Persisted</span>
              <strong>{runResult.totalQuestionsPersisted}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Skipped Duplicates</span>
              <strong>{runResult.totalSkippedRunDuplicates}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Generated Full Tests</span>
              <strong>{runResult.testSeriesGeneration.generatedNowFullTestCount}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Generated Sectional Tests</span>
              <strong>{runResult.testSeriesGeneration.generatedNowSectionalTestCount}</strong>
            </div>
            <div className="ca-admin-stat-card">
              <span>Generated PYQ Tests</span>
              <strong>{runResult.testSeriesGeneration.generatedNowPyqTestCount}</strong>
            </div>
          </div>

          <div className="ca-admin-table-wrap" style={{ marginTop: 16 }}>
            <table className="ca-admin-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Topic</th>
                  <th>Requested</th>
                  <th>Generated</th>
                  <th>Persisted</th>
                  <th>Complete</th>
                  <th>Success</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {runResult.topicResults.map((row, index) => (
                  <tr key={`${row.subject}-${row.topic}-${index}`}>
                    <td data-label="Subject">{row.subject || "NA"}</td>
                    <td data-label="Topic">{row.topic || "NA"}</td>
                    <td data-label="Requested">{row.requestedTotal}</td>
                    <td data-label="Generated">{row.generatedCount}</td>
                    <td data-label="Persisted">{row.persistedTotal}</td>
                    <td data-label="Complete">{row.complete ? "Yes" : "No"}</td>
                    <td data-label="Success">{row.success ? "Yes" : "No"}</td>
                    <td data-label="Error">{row.error || "NA"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}

export default AdminSyllabusAutomationPage;
